import React, { useRef, useEffect } from 'react';
import { X, Download, ExternalLink, FileSpreadsheet } from 'lucide-react';
import { downloadFile } from '../../utils/fileUtils';
import type { FileAttachment } from '../../types/chat';
import './FileViewerModal.css';

interface FileViewerModalProps {
  file: FileAttachment | null;
  isOpen: boolean;
  onClose: () => void;
}

export const FileViewerModal: React.FC<FileViewerModalProps> = ({
  file,
  isOpen,
  onClose,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    if (!isOpen && iframeRef.current) {
      try {
        iframeRef.current.src = 'about:blank';
      } catch {
        console.error('Error clearing iframe');
      }
    }
  }, [isOpen]);

  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      if (event.message?.includes('EwaTS') || 
          event.message?.includes('MicrosoftAjaxDS') ||
          event.filename?.includes('officeonline') ||
          event.filename?.includes('officeapps.live.com')) {
        event.preventDefault();
        return false;
      }
    };

    window.addEventListener('error', handleError);
    return () => {
      window.removeEventListener('error', handleError);
    };
  }, []);

  if (!isOpen || !file) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleClose = () => {
    if (iframeRef.current) {
      try {
        iframeRef.current.src = 'about:blank';
      } catch {
        console.error('Error clearing iframe');
      }
    }
    setTimeout(() => {
      onClose();
    }, 100);
  };

  const handleDownload = () => {
    downloadFile(file);
  };

  const handleOpenInNewTab = () => {
    if (file.url) {
      const viewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(file.url)}`;
      window.open(viewerUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const getFileIcon = () => {
    return <FileSpreadsheet size={48} />;
  };

  const getPreviewUrl = () => {
    if (file.url) {
      const encodedUrl = encodeURIComponent(file.url);
      return `https://view.officeapps.live.com/op/embed.aspx?src=${encodedUrl}&wdAllowInteractivity=false&wdHideGridlines=true&wdHideHeaders=true`;
    }
    return null;
  };

  const previewUrl = getPreviewUrl();

  return (
    <div className="file-viewer-modal" onClick={handleBackdropClick}>
      <div className="file-viewer-modal__content">
        <div className="file-viewer-modal__header">
          <div className="file-viewer-modal__title">
            <span className="file-viewer-modal__name" title={file.name}>
              {file.name}
            </span>
            <span className="file-viewer-modal__size">
              {file.size ? `${(file.size / 1024).toFixed(1)} KB` : ''}
            </span>
          </div>
          
          <div className="file-viewer-modal__actions">
            {file.url && (
              <button
                onClick={handleOpenInNewTab}
                className="file-viewer-modal__action-button"
                title="Открыть в новой вкладке"
              >
                <ExternalLink size={20} />
              </button>
            )}
            <button
              onClick={handleDownload}
              className="file-viewer-modal__action-button"
              title="Скачать файл"
            >
              <Download size={20} />
            </button>
            <button
              onClick={handleClose}
              className="file-viewer-modal__action-button file-viewer-modal__action-button--close"
              title="Закрыть"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="file-viewer-modal__body">
          {previewUrl ? (
            <div className="file-viewer-modal__preview">
              <iframe
                ref={iframeRef}
                src={previewUrl}
                className="file-viewer-modal__iframe"
                title={`Предпросмотр ${file.name}`}
                allow="fullscreen"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
                sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-modals"
                onLoad={() => {
                  try {
                    const iframe = iframeRef.current;
                    if (iframe?.contentWindow) {
                      iframe.contentWindow.addEventListener('error', (e) => {
                        e.stopPropagation();
                        return false;
                      }, true);
                    }
                  } catch {
                    console.error('Error loading iframe');
                  }
                }}
              />
            </div>
          ) : (
            <div className="file-viewer-modal__file-info">
              <div className="file-viewer-modal__icon">
                {getFileIcon()}
              </div>
              <div className="file-viewer-modal__file-details">
                <h3 className="file-viewer-modal__file-name">{file.name}</h3>
                <p className="file-viewer-modal__file-type">{file.type}</p>
                <p className="file-viewer-modal__file-size">
                  {file.size ? `${(file.size / 1024).toFixed(1)} KB` : 'Размер неизвестен'}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FileViewerModal;
